import { forwardRef, ReactNode, useImperativeHandle, useState, useEffect } from 'react'
import cx from 'classnames'

import Styles from './InlineEditable.module.scss'
import PropertyValueStyles from '../../PropertyValue.module.scss'
import { Property, PropertyType, PropertyValue } from 'api/clients/PropertyClient'
import { HTMLInputEvent } from 'types'
import {
  SelectPropertyEventDetails,
  selectPropertyEventName,
} from 'screens/Finder/components/FileTree/events/selectProperty'
import { ReactComponent as EditIcon } from 'common/PropertyItem/icons/edit__16.svg'
import { PastePropertyEvent, pastePropertyEventName } from 'screens/Finder/components/FileTree/events/pasteProperty'
import { useEvent } from 'react-use'

interface Props {
  property: Property
  propertyValue: PropertyValue
  children: ReactNode
  onCreate?: (propertyValue: PropertyValue) => void
  onUpdate?: (propertyValue: PropertyValue) => void
  isEditing?: boolean
  setIsEditing: (isEditing: boolean) => void
  disabled: boolean
  assigneeId?: string
  isBlank?: boolean
  valueClassName?: string
}

export type InlineEditableHandles = {
  /**
   * Clears the input value without changing the editing state
   * Due to the order in which events are dispatched, it is possible to clear and then immediately add a character,
   * which we exploit to allow the user to edit the value by entering text seemingly directly, without having to click
   * first
   */
  clear: () => void
}

export const InlineEditable = forwardRef<InlineEditableHandles, Props>(
  (
    {
      property,
      propertyValue,
      onCreate,
      onUpdate,
      isEditing,
      setIsEditing,
      disabled,
      assigneeId,
      isBlank = false,
      children,
      valueClassName,
    },
    ref,
  ) => {
    const [updatedValue, setUpdatedValue] = useState(propertyValue?.value)

    useEffect(() => {
      setUpdatedValue(propertyValue?.value)
    }, [propertyValue?.value])

    const handleInputChange = (e: HTMLInputEvent) => {
      setUpdatedValue(e.target.value)
    }

    useImperativeHandle(ref, () => ({
      clear: () => {
        setUpdatedValue('')
      },
    }))

    const resetInput = () => {
      setIsEditing(false)
      setUpdatedValue(propertyValue?.value)
    }

    const handleCreateOrUpdate = (value?: string) => {
      if (propertyValue?.id) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        onUpdate!({
          ...propertyValue,
          value: value ?? updatedValue,
        })
        return
      }

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      onCreate!({
        value: value ?? updatedValue,
        property_id: property.id,
      } as PropertyValue)
    }

    useEvent(
      pastePropertyEventName,
      (e) => {
        const evt = e as PastePropertyEvent
        if (evt.detail.propertyId === property.id && evt.detail.assigneeId === assigneeId) {
          switch (property.type) {
            case PropertyType.Number:
              if (!isNaN(Number(evt.detail.payload))) {
                handleCreateOrUpdate(Number(evt.detail.payload).toString())
              }
              break
            case PropertyType.Link:
            case PropertyType.Text:
              handleCreateOrUpdate(evt.detail.payload)
              break
          }
        }
      },
      document.body,
    )

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (property.type === PropertyType.Number && ['ArrowUp', 'ArrowDown'].includes(e.key)) {
        // disable the default behaviour of the arrow keys (increment/decrement) when editing a number input
        e.preventDefault()
        return
      }
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Home', 'End'].includes(e.key)) {
        e.stopPropagation()
      }
      switch (e.key) {
        case 'Enter':
        case 'Tab':
          handleCreateOrUpdate()
          setIsEditing(false)
          break
        case 'Escape':
          resetInput()
          break
        default:
          return
      }
      if (e.key === 'Tab') {
        document.body.dispatchEvent(
          new CustomEvent<SelectPropertyEventDetails>(selectPropertyEventName, {
            bubbles: false,
            detail: e.shiftKey ? 'Previous' : 'Next',
          }),
        )
        return
      }
      if (e.key === 'Enter') {
        document.body.dispatchEvent(
          new CustomEvent<SelectPropertyEventDetails>(selectPropertyEventName, {
            bubbles: false,
            detail: 'Down',
          }),
        )
      }
    }

    if (isEditing) {
      return (
        <input
          autoFocus
          type={property.type === PropertyType.Number ? 'number' : 'text'}
          className={cx(Styles.input, Styles[property.type])}
          value={updatedValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            handleCreateOrUpdate()
            setIsEditing(false)
          }}
        />
      )
    }

    return (
      <div className={cx(Styles.inlineViewWrapper, Styles[property.type])}>
        <button
          disabled={disabled}
          className={cx(PropertyValueStyles.value, valueClassName, Styles.hideOverflow, {
            [Styles.editable]: !disabled,
          })}
          onClick={() => setIsEditing(true)}
        >
          {children}
        </button>
        {property.type === PropertyType.Link && !disabled && !isBlank && (
          <button className={Styles.editBtn} onClick={() => setIsEditing(true)}>
            <EditIcon />
          </button>
        )}
      </div>
    )
  },
)
InlineEditable.displayName = 'InlineEditable'
